import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Col, Row } from 'antd';
import { H2 } from 'components/new-ui/base/typography/styles';
import CreateUpdateNoteForm from 'components/new-ui/composed/note/CreateUpdateNoteForm';
import Note from 'components/new-ui/composed/note/Note';
import { NoteFormFields } from 'components/new-ui/composed/note/types';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { ListPayload } from 'core/http/response';
import { addNoteToJob, deleteJobNote, getJobNotes, updateJobNote } from 'store/jobs/client';
import Notification from 'components/ui/base/notification/Notification';
import { JobNote } from 'store/jobs/job';
import useSession from 'hooks/useSession';
import { getErrorMessage } from 'utils/getErrorMessage';
import { HEADER_AND_NAVBAR_CONTENT_HEIGHT_PX } from 'core/utils/constants';

const NotesTab = () => {
    const { userInfo } = useSession();
    const { jobId } = useParams();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const [notes, setNotes] = useState<ListPayload<JobNote>>();
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const fetchNotes = useCallback(
        async (id: string) => {
            showGlobalOverlay({ type: 'LoadingOverlay' });
            try {
                const list = await getJobNotes(id);
                setNotes(list);
            } catch (err) {
                const errorMessage = getErrorMessage(err);
                Notification({
                    description: errorMessage || 'Could not get notes for this job',
                    messageTitle: 'Error',
                    type: 'error',
                    placement: 'top',
                });
            } finally {
                hideGlobalOverlay();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleSubmit = useCallback(
        async (values: NoteFormFields & { id?: string }): Promise<boolean> => {
            if (!jobId) {
                Notification({
                    messageTitle: 'Error',
                    description: 'Could not get job id',
                    type: 'error',
                    placement: 'top',
                });
                return false;
            }
            setSubmitting(true);
            showGlobalOverlay({ type: 'LoadingOverlay' });

            try {
                if (editingNoteId) {
                    await updateJobNote(jobId, editingNoteId, {
                        title: values.title,
                        note: values.content,
                    });
                } else {
                    await addNoteToJob(jobId, {
                        note: values.content,
                        title: values.title,
                    });

                    Notification({
                        messageTitle: 'Success',
                        description: 'Note created',
                        type: 'success',
                        placement: 'top',
                    });
                }
                setEditingNoteId(null);
                await fetchNotes(jobId);
                return true;
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                Notification({
                    messageTitle: 'Error',
                    description: errorMessage || editingNoteId ? 'Could not update note' : 'Could not create note',
                    type: 'error',
                    placement: 'top',
                });
                return false;
            } finally {
                hideGlobalOverlay();
                setSubmitting(false);
            }
        }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [editingNoteId, fetchNotes, jobId]
    );

    const handleDeleteNote = async (noteId: string) => {
        if (!jobId) {
            Notification({
                messageTitle: 'Error',
                description: 'Could not get job data',
                type: 'error',
                placement: 'top',
            });
            return;
        }

        try {
            showGlobalOverlay({ type: 'LoadingOverlay' });
            await deleteJobNote(jobId, noteId);

            Notification({
                messageTitle: 'Success',
                description: 'Note deleted',
                type: 'success',
                placement: 'top',
            });

            await fetchNotes(jobId);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                messageTitle: 'Error',
                description: errorMessage || 'Could not delete note',
                type: 'error',
                placement: 'top',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    const handleEdit = (noteId: string) => {
        setEditingNoteId(noteId);
    };

    const initialFormValues: NoteFormFields | undefined = useMemo(() => {
        if (!editingNoteId || !notes?.list) return undefined;
        const note = notes.list.find((_note) => _note.id === editingNoteId);
        return note ? { title: note.title ?? '', content: note.note ?? '' } : undefined;
    }, [editingNoteId, notes?.list]);

    useEffect(() => {
        if (!jobId) {
            return Notification({
                type: 'error',
                messageTitle: 'Error',
                description: 'Missing job information.',
                placement: 'top',
            });
        }

        fetchNotes(jobId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    return (
        <>
            <Row>
                <Col
                    lg={16}
                    style={{
                        // Display an inner vertical scroller for the notes container
                        overflowY: 'auto',
                        height: `calc(100vh - ${HEADER_AND_NAVBAR_CONTENT_HEIGHT_PX}px)`,
                        paddingRight: 24,
                    }}
                >
                    <H2 style={{ marginBottom: 32 }}>Notes</H2>
                    {notes?.list?.map((note) => (
                        <Note
                            note={{
                                content: note.note,
                                title: note.title,
                                id: note.id,
                                createdAt: note.createdAt,
                                createdBy: note.creator.fullName,
                            }}
                            isOwnNote={note.creator.id === userInfo?.individualId}
                            hideBorderBottom
                            onDelete={() => handleDeleteNote(note.id)}
                            onEdit={() => handleEdit(note.id)}
                        />
                    ))}
                </Col>

                <Col lg={8} style={{ paddingLeft: 24 }}>
                    <H2 style={{ marginBottom: 32 }}>{editingNoteId ? 'Edit Note' : 'Add Note'}</H2>
                    <CreateUpdateNoteForm
                        key={editingNoteId ?? 'create'}
                        initialValues={initialFormValues}
                        submitting={submitting}
                        onSubmit={handleSubmit}
                        onCancel={() => setEditingNoteId(null)}
                    />
                </Col>
            </Row>
        </>
    );
};

export default NotesTab;
